/**
 * Busca por município (pedido do usuário, 08/10/2026): o município escolhido —
 * destacado no mapa e enquadrado —, a localização do GPS (só quando a pessoa
 * pede) e o estado da busca. Registra o codec de URL `b` (de busca) no import —
 * `ui/modules.tsx` importa este arquivo antes de `readUrlState()`: o link
 * leva o município (`b=1506807`), o destaque e o enquadramento voltam com ele.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';

export interface MunicipioEscolhido {
  /** Código do IBGE (7 dígitos). */
  cd: string;
  nome: string;
  uf: string;
}

/** O que a localização pelo GPS está fazendo. */
export type EstadoDoGps = 'parado' | 'localizando' | 'ok' | 'negado' | 'fora' | 'erro';

export interface BuscaState {
  municipio: MunicipioEscolhido | null;
  /** Ponto da localização (projeção do mapa), quando a pessoa pediu. Não vai para o link. */
  local: [number, number] | null;
  gps: EstadoDoGps;
  /** O município veio do link e ainda não foi enquadrado (o mapa e o índice podem não estar prontos). */
  enquadrarAoCarregar: boolean;
  escolher: (m: MunicipioEscolhido | null) => void;
  setLocal: (local: [number, number] | null, gps: EstadoDoGps) => void;
  setGps: (gps: EstadoDoGps) => void;
}

export const useBuscaStore = create<BuscaState>()((set) => ({
  municipio: null,
  local: null,
  gps: 'parado',
  enquadrarAoCarregar: false,
  escolher: (municipio) => set(municipio ? { municipio } : { municipio: null, local: null, gps: 'parado' }),
  setLocal: (local, gps) => set({ local, gps }),
  setGps: (gps) => set({ gps }),
}));

/** `b` = código do IBGE do município destacado (7 dígitos), ou nada. */
export function encodeBusca(m: MunicipioEscolhido | null): string | null {
  return m ? m.cd : null;
}

export function decodeBusca(valor: string): string | null {
  return /^\d{7}$/.test(valor) ? valor : null;
}

registerUrlCodec({
  key: 'b',
  encode: () => encodeBusca(useBuscaStore.getState().municipio),
  // Nas preferências salvas não: abrir o site amanhã já aproximado num município seria estranho.
  persist: () => null,
  decode: (valor) => {
    const cd = decodeBusca(valor);
    // O nome chega com o índice (`busca/municipios.ts`); até lá, o código basta para destacar.
    if (cd) useBuscaStore.setState({ municipio: { cd, nome: '', uf: '' }, enquadrarAoCarregar: true });
  },
  subscribe: (onChange) =>
    useBuscaStore.subscribe((a, b) => {
      if (a.municipio?.cd !== b.municipio?.cd) onChange();
    }),
});
